import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { Observable, BehaviorSubject, throwError, of } from 'rxjs';
import { map, catchError, tap, switchMap } from 'rxjs/operators';
import { 
  ScheduledPaymentModel, 
  CreateScheduledPaymentRequest,
  UpdateScheduledPaymentRequest
} from '../models/scheduled-payment.model';
import { environment } from '../../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class ScheduledPaymentsService {
  
  private apiUrl = `${environment.FREELY_API_URL}/ScheduledPayments`;
  private http = inject(HttpClient);
  
  // Subject to notify subscribers of data changes
  private scheduledPaymentsSubject = new BehaviorSubject<ScheduledPaymentModel[]>([]);
  public scheduledPayments$ = this.scheduledPaymentsSubject.asObservable();
  
  private currentCustomerId: string | null = null;

  /**
   * Get all scheduled payments for a customer
   */
  getScheduledPayments(customerId: string): Observable<ScheduledPaymentModel[]> {
    this.currentCustomerId = customerId;
    
    return this.http.get<any>(`${this.apiUrl}/${customerId}`)
      .pipe(
        map(response => {
          // Check if response is null/undefined
          if (!response) {
            return [];
          }
          
          // Check if response has a 'data' property (backend wrapper)
          let paymentsArray: any[];
          if (response.data && Array.isArray(response.data)) {
            paymentsArray = response.data;
          } else if (Array.isArray(response)) {
            paymentsArray = response;
          } else {
            return [];
          }

          return paymentsArray.map(p => this.mapBackendToFrontend(p));
        }),
        tap(payments => {
          this.scheduledPaymentsSubject.next(payments);
        }),
        catchError((error) => {
          this.scheduledPaymentsSubject.next([]);
          return of([]); // Return empty array on error instead of throwing
        })
      );
  }
  
  /**
   * Refresh the current customer's scheduled payments
   */
  private refreshCurrentCustomer(): Observable<ScheduledPaymentModel[]> {
    if (this.currentCustomerId) {
      return this.getScheduledPayments(this.currentCustomerId);
    }
    return of([]);
  }

  /**
   * Get a single scheduled payment by ID with history
   */
  getScheduledPayment(customerId: string, scheduledPaymentId: string): Observable<ScheduledPaymentModel> {
    return this.http.get<any>(`${this.apiUrl}/${customerId}/${scheduledPaymentId}`)
      .pipe(
        map(response => {
          // Backend wraps response in {success: true, data: payment}
          const payment = response.data || response;
          const mapped = this.mapBackendToFrontend(payment);
          return mapped;
        }),
        catchError(this.handleError)
      );
  }

  /**
   * Create a new scheduled payment
   */
  createScheduledPayment(customerId: string, request: CreateScheduledPaymentRequest): Observable<ScheduledPaymentModel> {
    const backendRequest = this.mapFrontendToBackend(request);
    
    return this.http.post<any>(`${this.apiUrl}/${customerId}`, backendRequest)
      .pipe(
        map(payment => this.mapBackendToFrontend(payment)),
        switchMap(createdPayment => {
          // Refresh the list and return the created payment
          return this.refreshCurrentCustomer().pipe(
            map(() => createdPayment)
          );
        }),
        catchError(this.handleError)
      );
  }

  /**
   * Update a scheduled payment
   */
  updateScheduledPayment(
    customerId: string, 
    scheduledPaymentId: string, 
    request: UpdateScheduledPaymentRequest
  ): Observable<ScheduledPaymentModel> {
    // Format dates before sending to backend
    const backendRequest: any = {
      ...request,
      startDate: request.startDate ? this.formatDateForBackend(request.startDate) : undefined,
      endDate: request.endDate ? this.formatDateForBackend(request.endDate) : undefined
    };
    
    // Remove undefined fields to avoid sending null values
    Object.keys(backendRequest).forEach(key => {
      if (backendRequest[key] === undefined) {
        delete backendRequest[key];
      }
    });
    
    return this.http.put<any>(`${this.apiUrl}/${customerId}/${scheduledPaymentId}`, backendRequest)
      .pipe(
        map(payment => this.mapBackendToFrontend(payment)),
        switchMap(updatedPayment => {
          // Refresh the list and return the updated payment
          return this.refreshCurrentCustomer().pipe(
            map(() => {
              return updatedPayment;
            })
          );
        }),
        catchError(this.handleError)
      );
  }

  /**
   * Delete a scheduled payment
   */
  deleteScheduledPayment(customerId: string, scheduledPaymentId: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${customerId}/${scheduledPaymentId}`)
      .pipe(
        switchMap(() => {
          // Refresh the list after deletion
          return this.refreshCurrentCustomer().pipe(
            map(() => undefined)
          );
        }),
        catchError(this.handleError)
      );
  }

  /**
   * Pause a scheduled payment
   */
  pauseScheduledPayment(customerId: string, scheduledPaymentId: string): Observable<ScheduledPaymentModel> {
    return this.http.post<any>(`${this.apiUrl}/${customerId}/${scheduledPaymentId}/Pause`, {})
      .pipe(
        map(payment => this.mapBackendToFrontend(payment)),
        switchMap(pausedPayment => {
          // Refresh the list and return the paused payment
          return this.refreshCurrentCustomer().pipe(
            map(() => {
              return pausedPayment;
            })
          );
        }),
        catchError(this.handleError)
      );
  }

  /**
   * Resume a scheduled payment
   */
  resumeScheduledPayment(customerId: string, scheduledPaymentId: string): Observable<ScheduledPaymentModel> {
    return this.http.post<any>(`${this.apiUrl}/${customerId}/${scheduledPaymentId}/Resume`, {})
      .pipe(
        map(payment => this.mapBackendToFrontend(payment)),
        switchMap(resumedPayment => {
          // Refresh the list and return the resumed payment
          return this.refreshCurrentCustomer().pipe(
            map(() => {
              return resumedPayment;
            })
          );
        }),
        catchError(this.handleError)
      );
  }

  /**
   * Skip the next scheduled payment
   */
  skipNextPayment(customerId: string, scheduledPaymentId: string): Observable<ScheduledPaymentModel> {
    return this.http.post<any>(`${this.apiUrl}/${customerId}/${scheduledPaymentId}/SkipNext`, {})
      .pipe(
        map(payment => this.mapBackendToFrontend(payment)),
        switchMap(skippedPayment => {
          // Refresh the list and return the skipped payment
          return this.refreshCurrentCustomer().pipe(
            map(() => {
              return skippedPayment;
            })
          );
        }),
        catchError(this.handleError)
      );
  }

  /**
   * Map backend response to frontend model
   */
  private mapBackendToFrontend(backendPayment: any): ScheduledPaymentModel {
    return {
      id: backendPayment.scheduledPaymentId,
      customerId: backendPayment.customerId,
      tokenId: backendPayment.tokenId,
      paymentPlanType: backendPayment.paymentPlanType,
      frequency: backendPayment.frequency,
      weeklyAmount: backendPayment.weeklyAmount,
      totalAmount: backendPayment.totalAmount,
      numberOfWeeks: backendPayment.numberOfWeeks,
      calculatedWeeklyAmount: backendPayment.calculatedWeeklyAmount,
      capitalCostEnabled: backendPayment.capitalCostEnabled,
      capitalCostPercentage: backendPayment.capitalCostPercentage,
      capitalCostAmount: backendPayment.capitalCostAmount,
      startDate: this.parseDateOnly(backendPayment.startDate),
      endDate: backendPayment.endDate ? this.parseDateOnly(backendPayment.endDate) : undefined,
      nextExecutionDate: backendPayment.nextExecutionDate ? this.parseDateOnly(backendPayment.nextExecutionDate) : undefined,
      lastExecutionDate: backendPayment.lastExecutionDate ? new Date(backendPayment.lastExecutionDate) : undefined,
      status: backendPayment.status,
      executionCount: backendPayment.executionCount,
      remainingPayments: backendPayment.remainingPayments,
      skippedPayments: backendPayment.skippedPayments || 0,
      failedPayments: backendPayment.failedPayments || 0,
      retryCount: backendPayment.retryCount || 0,
      maxRetryCount: backendPayment.maxRetryCount || 3,
      lastErrorMessage: backendPayment.lastErrorMessage,
      totalPaid: backendPayment.totalPaid,
      remainingBalance: backendPayment.remainingBalance,
      referenceNumber: backendPayment.referenceNumber,
      description: backendPayment.description,
      cardInfo: undefined, // Will be populated by calling payment methods API if needed
      transactionHistory: backendPayment.history ? backendPayment.history.map((h: any) => ({
        id: h.historyId,
        executionDate: new Date(h.executionDate),
        scheduledDate: h.scheduledDate ? new Date(h.scheduledDate) : undefined,
        amount: h.amount,
        status: h.status,
        transactionId: h.transactionId,
        notes: h.notes
      })) : []
    };
  }

  /**
   * Map frontend request to backend format
   */
  private mapFrontendToBackend(request: CreateScheduledPaymentRequest): any {
    return {
      customerId: request.customerId,
      tokenId: request.tokenId,
      paymentPlanType: request.paymentPlanType,
      frequency: request.frequency || 'weekly',
      weeklyAmount: request.weeklyAmount,
      totalAmount: request.totalAmount,
      numberOfWeeks: request.numberOfWeeks,
      capitalCostEnabled: request.capitalCostEnabled || false,
      capitalCostPercentage: request.capitalCostPercentage,
      capitalCostAmount: request.capitalCostAmount,
      startDate: this.formatDateForBackend(request.startDate),
      endDate: request.endDate ? this.formatDateForBackend(request.endDate) : undefined,
      referenceNumber: request.referenceNumber,
      description: request.description,
      createdBy: 'admin' // This could be dynamically set from the auth service
    };
  }

  /**
   * Parse a date string (YYYY-MM-DD) as LOCAL midnight
   */
  private parseDateOnly(dateString: string | Date | undefined | null): Date {
    // Handle undefined/null
    if (!dateString) {
      return new Date();
    }
    
    // If already a Date object, return it
    if (dateString instanceof Date) {
      return dateString;
    }
    
    // If it's a string, parse it
    if (typeof dateString === 'string') {
      const [year, month, day] = dateString.split('-').map(Number);
      // Create date in LOCAL timezone midnight (month is 0-indexed)
      return new Date(year, month - 1, day);
    }
    
    // Fallback
    return new Date(dateString);
  }
  
  /**
   * Format date to YYYY-MM-DD for backend
   */
  private formatDateForBackend(date: Date | undefined | null): string {
    if (!date) {
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
    
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  /**
   * Handle HTTP errors
   */
  private handleError(error: HttpErrorResponse) {
    let errorMessage = 'An error occurred';
    
    if (error.error instanceof ErrorEvent) {
      // Client-side error
      errorMessage = `Error: ${error.error.message}`;
    } else {
      // Server-side error
      if (error.error && error.error.message) {
        errorMessage = error.error.message;
      } else if (error.error && error.error.details) {
        errorMessage = Array.isArray(error.error.details) 
          ? error.error.details.join(', ') 
          : error.error.details;
      } else {
        errorMessage = `Error Code: ${error.status}\nMessage: ${error.message}`;
      }
    }

    return throwError(() => new Error(errorMessage));
  }
}
